import React, { useCallback, useEffect, useRef } from 'react';
import { useSearchModifiers } from 'controllers/SearchController/hooks/useSearchModifiers';
import { Classifier } from 'types/generated/graphql';
import { useIsFilterApplied } from 'controllers/SearchController/hooks/useIsFilterApplied';
import { useApiFiltersContext } from 'contexts/ApiFiltersContext/ApiFiltersContext';
import { classifierToFiltersParams } from '../helpers/classifierToFiltersParams';
import { useClassifiersContext } from '../context/ClassifiersContext';

export const useSearchableClassifier = ({
    classifier,
}: {
    classifier: Classifier;
}) => {
    const classifierRef = useRef<HTMLDivElement>(null);

    const { registerPill, isClassifierHidden } = useClassifiersContext();

    useEffect(() => {
        if (classifierRef.current) {
            registerPill(classifier.id, classifierRef, classifier);
        }
    }, [classifier.id, registerPill, classifier]);

    // Check if this classifier is hidden using the context
    const isOverflowing = isClassifierHidden(classifier.id);

    const visibility: React.CSSProperties['visibility'] = isOverflowing
        ? 'hidden'
        : 'visible';

    const { code, type } = classifier;
    const filterType = classifierToFiltersParams(type);

    const { getFilterLabel } = useApiFiltersContext();
    const displayName = getFilterLabel(code, filterType);

    const isFilterApplied = useIsFilterApplied({
        filterType,
        code,
    });

    const { addFilter, removeFilter } = useSearchModifiers();

    const handleClick = useCallback(
        (event?: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
            event?.preventDefault();
            event?.stopPropagation();

            if (!code || !filterType) {
                return;
            }

            if (isFilterApplied) {
                removeFilter([[filterType, code ?? null]]);
            } else {
                addFilter([[filterType, code ?? null]]);
            }
        },
        [addFilter, code, filterType, removeFilter, isFilterApplied],
    );

    return {
        handleClick,
        code,
        filterType,
        displayName,
        visibility,
        classifierRef,
    };
};
